import { Product } from 'libs/http/api/content/content.types';

import { HardcodedCard, EmptyProductFields } from './data.types';

export type { HardcodedCard, EmptyProductFields };

export const PRICING_CARDS: Record<string, HardcodedCard> = {
  Growth: {
    description: 'No credit card required',
    free_value: true,
    best_value: false,
    featuresHeader: 'Per user, per month',
    features: [
      'Official Registry Data',
      'Directors & officers',
      'News & Activity',
      'Browser extension',
      'HubSpot, Salesforce, MS Dynamics integration',
    ],
  },
  Accelerate: {
    description: 'Perfect for scaling teams',
    free_value: false,
    best_value: true,
    featuresHeader: 'Per user, per month',
    features: ['20-Year Financial Filing History', 'Event Monitoring', 'Competitor Intelligence', 'List enrichment'],
  },
  Pro: {
    description: 'Deep intelligence for experts',
    free_value: false,
    best_value: false,
    featuresHeader: 'Per user, per month',
    features: ['UBO & Ownership Structure', 'Global Corporate Linkage', 'Funding / M&A data', 'API access'],
  },
};

const EMPTY_FIELDS: EmptyProductFields = {
  type: null,
  description: '',
  standalone: false,
  best_value: false,
  free_value: false,
  persist_price_visibility: false,
  price_general: 0,
  link_general: '',
  link_general_button: '',
  link_trial: '',
  link_trial_button: '',
  features: '',
  choices: '',
  pricing_feature_groups: [],
};

const BASE_URL = 'https://platform.globaldatabase.com/public/payment/summary';
const BASE_SIGNUP_URL = 'https://platform.globaldatabase.com/public/signup';

const createPaymentLink = (productId: number, frequency: 'month' | 'year'): string => {
  const params = new URLSearchParams({
    'data[0][type]': 'product',
    'data[0][data][country_id]': '',
    'data[0][data][product_id]': String(productId),
    'data[0][data][frequency]': frequency,
  });

  return `${BASE_URL}?${params.toString()}`;
};

const createProducts = (
  country: string,
  currency: string,
  signupSlug: string,
  accelerateProductId: number,
  proProductId: number,
): Product[] => [
  {
    ...EMPTY_FIELDS,
    country,
    name: 'Growth',
    currency,
    price_month: 0,
    price_year: 0,
    price_offer: 'Free',
    link: `${BASE_SIGNUP_URL}/${signupSlug}`,
    link_month_button: 'Get started for free',
    link_year: `${BASE_SIGNUP_URL}/${signupSlug}`,
    link_year_button: 'Get started for free',
  },
  {
    ...EMPTY_FIELDS,
    country,
    name: 'Accelerate',
    currency,
    price_month: 49,
    price_year: 39,
    price_offer: '',
    link: createPaymentLink(accelerateProductId, 'month'),
    link_month_button: 'Get started',
    link_year: createPaymentLink(accelerateProductId, 'year'),
    link_year_button: 'Get started',
  },
  {
    ...EMPTY_FIELDS,
    country,
    name: 'Pro',
    currency,
    price_month: 99,
    price_year: 79,
    price_offer: '',
    link: createPaymentLink(proProductId, 'month'),
    link_month_button: 'Get started',
    link_year: createPaymentLink(proProductId, 'year'),
    link_year_button: 'Get started',
  },
];

const createProductsNoFree = (
  country: string,
  currency: string,
  accelerateProductId: number,
  proProductId: number,
): Product[] =>
  createProducts(country, currency, '', accelerateProductId, proProductId).filter((p) => p.name !== 'Growth');

export const STATIC_PRODUCTS: Record<string, Product[]> = {
  'public-gb': createProducts('public-gb', '£', 'free_tier_uk', 195, 193),
  'public-italy': createProducts('public-italy', '€', 'free_tier_it', 197, 194),
  'public-ie': createProducts('public-ie', '€', 'free_tier_ie', 197, 194),
  'public-usa': createProducts('public-usa', '$', 'free_tier_us', 196, 192),
  'public-de': createProducts('public-de', '€', 'free_tier_de', 197, 194),
  'public-sg': createProductsNoFree('public-sg', '$', 196, 192),
};
